import { useEffect, useRef, useState, type FC } from "react";
import { X, Scan, Lightbulb } from "lucide-react";
import { Button } from "../../components/ui/button";
import { message } from "antd";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";

interface BarcodeScannerProps {
    onScan: (barcode: string) => void;
    onClose: () => void;
}

export const BarcodeScanner: FC<BarcodeScannerProps> = ({ onScan, onClose }) => {
    const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
    const hasScannedRef = useRef(false); // ✅ Ref for immediate synchronous check
    const [hasFlash, setHasFlash] = useState(false);
    const [isFlashOn, setIsFlashOn] = useState(false);
    const [isScanning, setIsScanning] = useState(true);
    const [scannedCode, setScannedCode] = useState<string>("");

    useEffect(() => {
        // Initialize the scanner on the 'reader' element with supported formats
        const html5QrCode = new Html5Qrcode("reader", {
            verbose: false,
            formatsToSupport: [
                Html5QrcodeSupportedFormats.EAN_13,
                Html5QrcodeSupportedFormats.EAN_8,
                Html5QrcodeSupportedFormats.UPC_A,
                Html5QrcodeSupportedFormats.UPC_E,
                Html5QrcodeSupportedFormats.CODE_128,
                Html5QrcodeSupportedFormats.CODE_39,
                Html5QrcodeSupportedFormats.CODE_93,
                Html5QrcodeSupportedFormats.ITF,
            ]
        });
        html5QrCodeRef.current = html5QrCode;

        const startScanner = async () => {
            try {
                const config = {
                    fps: 10, // Reduced FPS for controlled scanning speed
                    qrbox: { width: 280, height: 180 }, // Barcode shaped box
                    aspectRatio: 1.0,
                    disableFlip: false,
                };

                await html5QrCode.start(
                    { facingMode: "environment" },
                    config,
                    (decodedText) => {
                        // ✅ Use ref for immediate synchronous check - prevents multiple callbacks
                        if (hasScannedRef.current) {
                            return;
                        }

                        // Mark as scanned immediately (synchronous)
                        hasScannedRef.current = true;

                        setIsScanning(false);
                        setScannedCode(decodedText);

                        // 1. Haptic Feedback (Vibration)
                        if (navigator.vibrate) {
                            navigator.vibrate([100, 50, 100]); // Vibrate pattern
                        }

                        // 2. Visual Feedback - Flash green
                        const readerElement = document.getElementById("reader");
                        if (readerElement) {
                            readerElement.style.border = "4px solid #22c55e";
                            readerElement.style.boxShadow = "0 0 20px #22c55e";
                        }

                        // 3. Delay before closing to show feedback
                        setTimeout(() => {
                            handleStop().then(() => {
                                onScan(decodedText);
                            });
                        }, 1200); // 1.2 second delay so user sees the feedback
                    },
                    (_errorMessage) => {
                        // failure, usually ignored as it happens constantly during scanning
                    }
                );

                // Check for flash support after starting
                const track = (html5QrCode as any).getRunningTrack?.();
                if (track && 'getCapabilities' in track) {
                    const capabilities = (track as any).getCapabilities();
                    setHasFlash(!!capabilities.torch);
                }
            } catch (err) {
                console.error("Failed to start scanner:", err);
                message.warning("Could not access camera. Please check permissions.");
            }
        };

        startScanner();

        return () => {
            handleStop();
        };
    }, [onScan]);

    const handleStop = async () => {
        if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
            try {
                await html5QrCodeRef.current.stop();
                html5QrCodeRef.current.clear();
            } catch (err) {
                console.error("Failed to stop scanner:", err);
            }
        }
    };

    const toggleFlash = async () => {
        if (html5QrCodeRef.current && hasFlash) {
            try {
                const newState = !isFlashOn;
                await html5QrCodeRef.current.applyVideoConstraints({
                    advanced: [{ torch: newState } as any]
                });
                setIsFlashOn(newState);
            } catch (err) {
                console.error("Failed to toggle flash:", err);
            }
        }
    };

    const handleManualInput = () => {
        // Prevent multiple manual inputs
        if (hasScannedRef.current) return;

        const input = prompt("Please enter barcode manually:");
        if (input && input.trim()) {
            hasScannedRef.current = true;
            handleStop().then(() => {
                onScan(input.trim());
            });
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex flex-col bg-black overflow-hidden safe-area-inset-all">
            {/* Header */}
            <div className="relative flex items-center justify-between px-4 py-3 bg-black/80 backdrop-blur-md border-b border-white/10 z-20">
                <div className="flex items-center gap-2">
                    <Scan className="h-5 w-5 text-purple-400" />
                    <h2 className="text-lg font-semibold text-white">Scan Barcode</h2>
                </div>
                <button
                    onClick={() => {
                        handleStop().then(() => {
                            onClose();
                        });
                    }}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition-colors hover:bg-white/20 active:scale-95"
                >
                    <X size={20} />
                </button>
            </div>

            {/* Camera View Area */}
            <div className="relative flex-1 flex flex-col items-center justify-center bg-black overflow-hidden">
                {/* The Reader element required by html5-qrcode */}
                <div id="reader" className="w-full h-full object-cover"></div>

                {/* Scanning Overlay (Custom Graphic) */}
                <div className="absolute inset-0 z-10 pointer-events-none flex flex-col items-center justify-center">

                    {/* Viewport hole */}
                    <div className={`relative w-[280px] h-[180px] border-2 rounded-2xl overflow-hidden shadow-[0_0_0_1000px_rgba(0,0,0,0.5)] transition-all duration-300 ${!isScanning ? 'border-green-500 shadow-[0_0_20px_rgba(34,197,94,0.8)]' : 'border-white/20'
                        }`}>
                        {/* Corner markers */}
                        <div className={`absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 rounded-tl-lg transition-colors duration-300 ${!isScanning ? 'border-green-500' : 'border-purple-500'
                            }`}></div>
                        <div className={`absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 rounded-tr-lg transition-colors duration-300 ${!isScanning ? 'border-green-500' : 'border-purple-500'
                            }`}></div>
                        <div className={`absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 rounded-bl-lg transition-colors duration-300 ${!isScanning ? 'border-green-500' : 'border-purple-500'
                            }`}></div>
                        <div className={`absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 rounded-br-lg transition-colors duration-300 ${!isScanning ? 'border-green-500' : 'border-purple-500'
                            }`}></div>

                        {/* Scanning line animation - Hide when scanned */}
                        {isScanning && (
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-purple-400 to-transparent shadow-[0_0_8px_rgba(168,85,247,0.8)] animate-scan-line"></div>
                        )}

                        {/* Success checkmark */}
                        {!isScanning && (
                            <div className="absolute inset-0 flex items-center justify-center bg-green-500/20 animate-pulse">
                                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500">
                                    <svg className="h-10 w-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Instruction Text below box */}
                    <p className={`mt-8 px-6 py-2 rounded-full backdrop-blur-sm text-sm font-medium border transition-all duration-300 ${!isScanning
                        ? 'bg-green-500/80 text-white border-green-400'
                        : 'bg-black/60 text-white border-white/10'
                        }`}>
                        {!isScanning ? '✓ Scanned Successfully!' : 'Position barcode within the frame'}
                    </p>

                    {/* Scanned barcode display */}
                    {!isScanning && scannedCode && (
                        <p className="mt-3 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-compact-base font-medium tabular-nums text-white border border-white/20">
                            {scannedCode}
                        </p>
                    )}
                </div>

                {/* Flash Toggle Button */}
                {hasFlash && (
                    <button
                        onClick={toggleFlash}
                        className={`absolute bottom-10 right-10 z-20 flex h-12 w-12 items-center justify-center rounded-full backdrop-blur-md transition-all active:scale-95 ${isFlashOn ? "bg-yellow-400 text-black shadow-[0_0_15px_rgba(250,204,21,0.5)]" : "bg-white/10 text-white"
                            }`}
                    >
                        <Lightbulb size={24} className={isFlashOn ? "fill-current" : ""} />
                    </button>
                )}
            </div>

            {/* Bottom Actions */}
            <div className="relative bg-black/80 backdrop-blur-md px-4 py-6 space-y-4 border-t border-white/10 z-20"
                style={{ paddingBottom: 'max(24px, calc(env(safe-area-inset-bottom) + 16px))' }}>

                {/* Manual Input Button */}
                <Button
                    variant="outline"
                    className="h-12 w-full rounded-2xl text-sm font-semibold border-2 border-white/20 bg-white/5 text-white hover:bg-white/10 active:scale-[0.98] transition-all"
                    onClick={handleManualInput}
                >
                    Enter Barcode Manually
                </Button>
            </div>

            {/* Add Custom Animations */}
            <style>{`
        @keyframes scan-line {
          0% { top: 10%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 90%; opacity: 0; }
        }
        .animate-scan-line {
          animation: scan-line 2.5s ease-in-out infinite;
        }
        #reader {
          background-color: black !important;
        }
        #reader video {
          object-fit: cover !important;
          width: 100% !important;
          height: 100% !important;
        }
        #reader__status_span {
          display: none !important;
        }
        /* Hide html5-qrcode library's built-in gray scanning box & shaded region */
        #reader__scan_region > div {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
          outline: none !important;
        }
        #reader__scan_region > div > div {
          background: transparent !important;
          border: none !important;
          box-shadow: none !important;
        }
        #reader__scan_region canvas {
          display: none !important;
        }
        #reader__scan_region img {
          display: none !important;
        }
        #reader__dashboard {
          display: none !important;
        }
        #reader__header_message {
          display: none !important;
        }
        /* Hide any qr-shaded-region elements */
        [id*="qr-shaded-region"] {
          display: none !important;
        }
        #reader * {
          box-shadow: none !important;
        }
      `}</style>
        </div>
    );
};

export default BarcodeScanner;
